import type {
  ApiBlock,
  BlockType,
} from '@backmarket/http-api/src/api-specs-content/models/block'
import type { RichTextApiProps } from '@backmarket/http-api/src/api-specs-content/models/rich-text-content'
import type {
  Inline,
  Node,
  Block as RichTextBlock,
  Text,
} from '@contentful/rich-text-types'

import type { ExtendedHyperlink } from './contentful-rich-text-types-extension'

export type ArticleContentTitle = {
  title: string
  headingType: string
}

const BLOCKS_TO_IGNORE = new Set<Partial<BlockType>>([
  'Quiz',
  'EmailCollector',
  'Button',
  'DiscountBanner',
  'ListItem',
  'ModularCardImage',
])

export function findTitlesInRichtext(
  contentItems: (RichTextBlock | Inline | Text)[],
  nodeType?: string,
  params?: { merge: boolean },
): ArticleContentTitle[] {
  const titles: ArticleContentTitle[] = []
  let concatenation = ''
  for (let i = 0; i < contentItems.length; i += 1) {
    const item = contentItems[i]
    if (item.nodeType === 'heading-2' || item.nodeType === 'heading-3') {
      titles.push(
        ...findTitlesInRichtext(item.content, item.nodeType, { merge: true }),
      )
    }

    if (item.nodeType === 'text' && item.value.length) {
      concatenation += item.value

      const hasMoreTextNodes = contentItems
        .slice(i + 1)
        .some((node) => node?.nodeType === 'text' && node.value.length)

      if (!hasMoreTextNodes) {
        titles.push({ title: concatenation, headingType: nodeType ?? '' })
      }
    }

    if (item.nodeType === 'hyperlink') {
      const hyperlinkTitles = findTitlesInRichtext(item.content, nodeType)
      titles.push(...hyperlinkTitles)
    }
  }

  if (params?.merge && titles.length > 1) {
    let headingConcatenation = ''
    titles.forEach((item) => {
      headingConcatenation += item.title
    })

    return [{ title: headingConcatenation, headingType: titles[0].headingType }]
  }

  return titles
}

export function findTitlesInBlocks(blocks: ApiBlock[]) {
  const headings: ArticleContentTitle[] = []
  for (let i = 0; i < blocks.length; i += 1) {
    const { type, props } = blocks[i]

    // These block types don't use h2/h3 tags for the title field so we can ignore them
    if (BLOCKS_TO_IGNORE.has(type)) continue

    if (
      'title' in props &&
      typeof props.title === 'string' &&
      props.title?.length
    ) {
      headings.push({ title: props.title, headingType: 'heading-2' })
    } else if ('contentTitle' in props && props.contentTitle?.length) {
      headings.push({ title: props.contentTitle, headingType: 'heading-2' })
    } else if (type === 'RichText') {
      const { richText } = props as RichTextApiProps
      headings.push(...findTitlesInRichtext(richText.content))
    }
  }

  return headings
}

export function isNodeHyperlink(n: Node): n is ExtendedHyperlink {
  return !!n.data && 'uri' in n.data
}

export function findRichTextLinks(
  contentItems: Array<RichTextBlock | Inline | Text>,
  recursions: number = 0,
) {
  if (recursions >= 10) return []

  const currentRecursions = recursions + 1
  const links: { uri: string; text: string }[] = []

  contentItems.forEach((contentItem: RichTextBlock | Inline | Text) => {
    if (contentItem.nodeType === 'hyperlink' && contentItem?.data?.uri) {
      const { data, content } = contentItem
      let text = ''

      if (content?.[0]?.nodeType === 'text') {
        text = content?.[0]?.value || ''
      }

      links.push({ uri: data.uri, text })

      return
    }

    if ('content' in contentItem) {
      links.push(...findRichTextLinks(contentItem.content, currentRecursions))
    }
  })

  return links
}
